import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { AuditedStatementSlice, ReportData } from '../data/types';
import { ScreenSection } from '../primitives/ScreenPrimitives';
import { colors, fontFamily, typeScale } from '../data/designTokens';

const BS_SECTIONS: Array<{ name: string; match: RegExp }> = [
    { name: 'Equity & Liabilities', match: /equity|liabilit|borrowing|payab|provis|reserv|share/i },
    { name: 'Assets', match: /asset|receiv|invent|cash|invest|intang|tangib|advance|deposit/i },
];

const isBalanceSheet = (name: string) => /balance\s*sheet/i.test(name);
const isPnL = (name: string) => /p\s*&\s*l|profit|income/i.test(name);
const isRatios = (name: string) => /ratio/i.test(name);

const groupBalanceSheet = (rows: AuditedStatementSlice['rows']) => {
    const groups: Record<string, typeof rows> = {};
    rows.forEach((r) => {
        const bucket = BS_SECTIONS.find((s) => s.match.test((r.label ?? '') + ' ' + r.classificationName))?.name ?? 'Other';
        (groups[bucket] ??= []).push(r);
    });
    return groups;
};

const formatCell = (v?: string) => (v == null || v === '' ? '—' : v);

const yearColumns = (slice: AuditedStatementSlice) =>
    slice.headers.filter((h) => /^\d{4}$|^FY/i.test(h.key) || h.key !== 'classificationName').slice(-6);

// Move aggregated ("Total …") rows to the bottom of each group so the running
// figures read first and the summary lands last — easier to scan. Stable sort:
// non-aggregated keep their relative order, aggregated rows are appended.
const totalsLast = <T extends { isAggregated?: boolean }>(rows: T[]): T[] =>
    [...rows].sort((a, b) => Number(!!a.isAggregated) - Number(!!b.isAggregated));

// ──────────────────────────── Screen ────────────────────────────

const ScreenStatementTable: React.FC<{ slice: AuditedStatementSlice }> = ({ slice }) => {
    const cols = yearColumns(slice);
    const useGroups = isBalanceSheet(slice.name);
    const rawGroups = useGroups ? groupBalanceSheet(slice.rows) : { [slice.name]: slice.rows };
    const groups = Object.fromEntries(Object.entries(rawGroups).map(([k, v]) => [k, totalsLast(v)]));
    const isRatioTable = isRatios(slice.name);
    const thStyle: React.CSSProperties = {
        padding: '8px 6px',
        fontSize: 10,
        color: colors.muted,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        fontWeight: 700,
        textAlign: 'right',
        borderBottom: `1px solid ${colors.ink}`,
    };
    const tdStyle: React.CSSProperties = {
        padding: '6px',
        fontSize: 11.5,
        textAlign: 'right',
        color: colors.ink,
        borderBottom: `0.5px solid ${colors.hairlineSoft}`,
        fontVariantNumeric: 'tabular-nums',
    };
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
                <tr>
                    <th style={{ ...thStyle, textAlign: 'left', width: '32%' }}>{isRatioTable ? 'Ratio' : 'Classification'}</th>
                    {cols.map((h) => (
                        <th key={h.key} style={thStyle}>
                            {h.name}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Object.entries(groups).map(([groupName, rows]) => (
                    <React.Fragment key={groupName}>
                        {useGroups ? (
                            <tr>
                                <td
                                    colSpan={cols.length + 1}
                                    style={{
                                        padding: '6px',
                                        fontSize: 10,
                                        letterSpacing: 1,
                                        color: colors.ink00,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        background: colors.surfaceAlt,
                                    }}
                                >
                                    {groupName}
                                </td>
                            </tr>
                        ) : null}
                        {rows.map((r, i) => (
                            <tr key={i} style={{ background: r.isAggregated ? colors.surfaceAlt : undefined }}>
                                <td
                                    style={{
                                        ...tdStyle,
                                        textAlign: 'left',
                                        paddingLeft: r.subLabel ? 14 : 6,
                                        color: colors.inkSoft,
                                        fontWeight: r.isAggregated ? 600 : 400,
                                    }}
                                >
                                    {r.classificationName}
                                </td>
                                {cols.map((h) => (
                                    <td key={h.key} style={tdStyle}>
                                        {formatCell(r.values[h.key])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </React.Fragment>
                ))}
            </tbody>
        </table>
    );
};

const ScreenAuditedStatements: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const audited = data.auditedStatements;
    const provisional = data.provisionalStatements;
    const isProvisional = !audited || audited.statements.length === 0;
    const as = isProvisional ? provisional : audited;
    if (!as || as.statements.length === 0) return null;
    const datasetSuffix = isProvisional
        ? 'Provisional (management-supplied)'
        : as.hasConsolidated && as.hasStandalone
        ? 'Standalone & Consolidated available'
        : as.hasConsolidated
        ? 'Consolidated only'
        : as.hasStandalone
        ? 'Standalone only'
        : '';
    const countLabel = `${as.statements.length} statement${as.statements.length > 1 ? 's' : ''}`;
    const subtitle = datasetSuffix ? `${countLabel} · ${datasetSuffix}` : countLabel;
    return (
        <ScreenSection
            kicker="Section · Statements"
            title={isProvisional ? 'Provisional Financial Statements' : 'Audited Financial Statements'}
            subtitle={subtitle}
            index={index}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    fontSize: 10,
                    color: colors.muted,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    marginTop: 2,
                    marginBottom: 4,
                }}
            >
                All amounts in ₹
            </div>
            {/* All slices rendered one after another with a sub-heading each — no
                tab switching. The previous tabbed view only mounted the active
                slice, so the server PDF (which captures this DOM) silently lost
                every other statement. */}
            {as.statements.map((s, i) => (
                <div key={s.name} style={{ marginTop: i === 0 ? 4 : 24 }}>
                    {/* break-after:avoid keeps the statement sub-heading on the same
                        sheet as the first rows of its table — without it the heading
                        orphans onto a near-blank page when the table breaks. */}
                    <div
                        style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: colors.ink00,
                            marginBottom: 2,
                            breakAfter: 'avoid',
                            pageBreakAfter: 'avoid',
                        }}
                    >
                        {s.name}
                    </div>
                    <ScreenStatementTable slice={s} />
                </div>
            ))}
        </ScreenSection>
    );
};

export const auditedStatementsModule: ModuleDefinition = {
    meta: {
        id: 'auditedStatements' as any,
        title: 'Audited financial statements',
        description: 'Balance Sheet, P&L, Cashflow & Financial Ratios from audited filings.',
        dataSource: 'polus/insights/financialsAuditedDataRange',
    },
    // Module renders if EITHER audited or provisional statements are present —
    // the body falls through to provisional when audited is empty.
    hasData: (d) =>
        (!!d.auditedStatements && d.auditedStatements.statements.length > 0) ||
        (!!d.provisionalStatements && d.provisionalStatements.statements.length > 0),
    Screen: ScreenAuditedStatements,
};
